import classNames from 'classnames';
import React, { MutableRefObject, useRef, useState } from 'react';
import LoadingSpinner from 'src/common/components/loading-spinner';
import { BoxShareLinkInterface } from 'src/common/interfaces/integrations/box.interface';
import { useNotificationMessage } from 'src/common/notification/message.hook';
import { FullscreenModal } from 'src/editor/content-creation/components/fullscreen-modal';
import { BoxIntegrationIcon, CloseSmallIcon } from 'src/monet/icons';
import { functionNoop } from 'src/utils/function/noop';
import { useBoxIntegration } from 'src/utils/use-box-integration.hook';

interface Props {
  openRef: MutableRefObject<(() => void) | undefined>;
  onBoxFilePick: (boxLink: BoxShareLinkInterface) => void;
  isEditor?: boolean;
}

const LibraryBoxStatus = {
  Idle: 0,
  Loading: 1,
  Success: 2,
  Fail: 3,
};

type LibraryStatusType = typeof LibraryBoxStatus[keyof typeof LibraryBoxStatus];

interface BoxListItem {
  [id: string]: {
    hasLink: boolean;
    sharedLink: string;
    name: string;
  };
}

export const DashboardLibraryContentPickBoxFileModal = ({ openRef, onBoxFilePick, isEditor = false }: Props) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [status, setStatus] = useState<LibraryStatusType>(LibraryBoxStatus.Idle);
  const [boxItems, setBoxItems] = useState<BoxListItem>({});
  const { setErrorNotification } = useNotificationMessage();

  const modalCloseRef = useRef(functionNoop);

  const boxIntegration = useBoxIntegration();

  const onBoxFilePicked = async (id: string) => {
    const boxLink = boxItems[id];
    if (boxLink.hasLink) {
      onBoxFilePick({
        id,
        url: boxLink.sharedLink,
        name: boxLink.name,
      });
      return;
    }
    setIsProcessing(true);
    try {
      const fileShareLink = await boxIntegration.getShareLink(id);
      onBoxFilePick({
        id,
        url: fileShareLink.url,
        name: boxLink.name,
      });
    } catch (err) {
      console.error(err);
    } finally {
      setIsProcessing(false);
    }
  };

  const initBox = async () => {
    try {
      const files = await boxIntegration.fetchAllFiles();
      const shareLinks = await boxIntegration.getFileShareLinks(files);

      const boxList = files.reduce(
        (acc, file) => {
          const shareLink = shareLinks.find((link) => link.id === file.id);

          acc[file.id] = {
            hasLink: !!shareLink,
            sharedLink: shareLink?.url || '',
            name: file.name,
          };

          return acc;
        },
        { ...boxItems }
      );

      setBoxItems(boxList);
      setStatus(LibraryBoxStatus.Success);
    } catch (err) {
      modalCloseRef.current();
      setErrorNotification('Failed to fetch Box files');
    }
  };

  const renderModalContent = () => {
    if (status === LibraryBoxStatus.Loading) {
      return <LoadingSpinner />;
    }
    const boxEntries = Object.entries(boxItems);

    return (
      <div className='flex flex-col gap-8'>
        <div className='flex flex-col gap-8'>
          <BoxIntegrationIcon className='w-12 h-6' />
          <div className='flex flex-col gap-4'>
            {boxEntries.map(([id, { name }]) => (
              <div key={id} className='flex gap-2 items-center justify-between'>
                <span className='truncate'>{name}</span>
                <div className='flex items-center gap-2 ml-auto'>
                  <button
                    type='button'
                    className='btn-bedrock-secondary px-4 py-1 space-x-1'
                    onClick={() => onBoxFilePicked(id)}
                  >
                    <span>Use</span>
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div
          className='absolute top-0 right-0'
          onClick={() => {
            modalCloseRef.current();
          }}
        >
          <CloseSmallIcon className='w-5 h-5 text-bedrock-p' />
        </div>
        <div className='flex'>
          <button
            type='button'
            disabled={isProcessing}
            className='btn-bedrock-primary px-4 py-1 ml-auto'
            onClick={() => {
              modalCloseRef.current();
            }}
          >
            <span>Ok</span>
          </button>
        </div>
      </div>
    );
  };

  return (
    <FullscreenModal
      wrapperClassName='min-w-96 min-h-40 m-auto'
      layoutStyle='desktop'
      openRef={openRef}
      onOpen={initBox}
      onClose={() => {
        setStatus(LibraryBoxStatus.Idle);
      }}
      closeRef={modalCloseRef}
      overlayClassName='bg-neue-glass-light neue-glass-backdrop-blur-light'
    >
      <div
        className={classNames('relative rounded-lg m-auto p-4 w-[40rem]', {
          'bg-neue-journey-bg-90': isEditor,
          'bg-white': !isEditor,
        })}
      >
        {renderModalContent()}
      </div>
    </FullscreenModal>
  );
};
